/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionHide';

const ActionHideManager: ActionManagerFn<'ActionHide'> = (
  platform: Platform,
): ActionManager<'ActionHide'> => {
  return {
    ...baseActionManager(platform, 'ActionHide'),
    defaultValue,
    props: {
      ...baseActionProps,
      targetItemId: {
        label: 'Target Item ID',
        type: 'ItemID',
      },
    },
    name: 'Hide UI Component',
    getDisplayText: (item) => item.props.targetItemId,
    icon: { iconName: 'eye-slash' },
    replaceableWith: ['ActionShow'],
    description: `**Overview**<br/>
The Hide UI Component action is a visibility management tool designed to programmatically conceal specific interface elements. It serves as a mechanism to dynamically control the layout of the application, allowing developers to hide components based on user interactions, data states, or permission levels.<br/><br/>
This component is designed to operate generically on any UI element that possesses a unique identifier (targetItemId). It functions as the direct counterpart to the "Show UI Component" action, enabling the creation of toggleable interfaces or conditional displays.

**Key Features**<br/>
* **Universal Targeting:** Accepts a generic ItemID, allowing it to target and hide a wide variety of UI components (e.g., buttons, input fields, entire containers, or grids) without strict type filtering.
* **Component Interchangeability:** Explicitly configured in the replaceableWith array to be easily swapped with ActionShow, streamlining the logic building process for toggle behaviors.
* **Registry Visibility:** The getDisplayText method is configured to show the specific targetItemId in the action list, providing immediate context on which element is being hidden.

**Properties**<br/>
* **Target Item ID (targetItemId):** A property of type ItemID that identifies the specific UI component to be hidden.

**Use Cases**<br/>
* **Conditional Logic:** Hiding a "Submit" button until a form is fully valid.
* **Progressive Disclosure:** Hiding complex "Advanced Settings" panels by default to keep the interface clean for standard users.
* **Role-Based Access:** Hiding administrative controls or sensitive data widgets for users with lower permission levels.`,
  };
};

export default ActionHideManager;
